import type { CSSProperties } from 'react';

// Liquid Glass Theme Utilities
// Provides Apple-inspired glass effect configurations, utilities, and CSS helpers.

export type GlassVariant = 'regular' | 'clear';
export type GlassIntensity = 'soft' | 'medium' | 'deep';

export interface GlassConfig {
  enabled: boolean;
  intensity: GlassIntensity;
  variant: GlassVariant;
}

// Glass intensity presets
export const GLASS_INTENSITY = {
  soft: {
    blur: 14,
    opacity: 0.48,
    borderOpacity: 0.38,
    highlightOpacity: 0.22,
    shadowOpacity: 0.14,
  },
  medium: {
    blur: 22,
    opacity: 0.58,
    borderOpacity: 0.48,
    highlightOpacity: 0.3,
    shadowOpacity: 0.2,
  },
  deep: {
    blur: 34,
    opacity: 0.68,
    borderOpacity: 0.58,
    highlightOpacity: 0.38,
    shadowOpacity: 0.28,
  },
} as const;

// Get glass background color based on variant and color mode
export function getGlassBackground(variant: GlassVariant, colorMode: 'light' | 'dark', opacity: number): string {
  if (variant === 'clear') {
    return colorMode === 'dark'
      ? `rgba(12, 18, 28, ${Math.max(opacity - 0.36, 0.18)})`
      : `rgba(255, 255, 255, ${Math.max(opacity - 0.28, 0.24)})`;
  }

  return colorMode === 'dark'
    ? `rgba(16, 23, 36, ${opacity * 0.82})`
    : `rgba(255, 255, 255, ${opacity})`;
}

// Get glass border color based on variant and color mode
export function getGlassBorder(variant: GlassVariant, colorMode: 'light' | 'dark', opacity: number): string {
  if (variant === 'clear') {
    return colorMode === 'dark'
      ? `rgba(255, 255, 255, ${opacity * 0.8})`
      : `rgba(255, 255, 255, ${opacity})`;
  }

  return colorMode === 'dark'
    ? `rgba(255, 255, 255, ${opacity * 0.78})`
    : `rgba(255, 255, 255, ${opacity})`;
}

// Generate complete glass style object
export function getGlassStyle(
  intensity: GlassIntensity,
  variant: GlassVariant,
  colorMode: 'light' | 'dark'
): CSSProperties {
  const config = GLASS_INTENSITY[intensity];
  const rimColor = colorMode === 'dark' ? '255, 255, 255' : '255, 255, 255';
  
  return {
    background: getGlassBackground(variant, colorMode, config.opacity),
    backdropFilter: `blur(${config.blur}px)`,
    WebkitBackdropFilter: `blur(${config.blur}px)`,
    border: `1px solid ${getGlassBorder(variant, colorMode, config.borderOpacity)}`,
    boxShadow: `
      0 18px 48px rgba(0, 0, 0, ${config.shadowOpacity}),
      inset 0 1px 0 rgba(${rimColor}, ${config.highlightOpacity}),
      inset 0 -1px 0 rgba(255, 255, 255, ${config.highlightOpacity * 0.45})
    `.trim(),
    color: colorMode === 'dark' ? 'rgb(248 250 252)' : 'rgb(15 23 42)',
  };
}

// Generate hover glass style (brighter, more prominent)
export function getGlassHoverStyle(
  intensity: GlassIntensity,
  variant: GlassVariant,
  colorMode: 'light' | 'dark'
): CSSProperties {
  const config = GLASS_INTENSITY[intensity];
  const hoverOpacity = Math.min(config.opacity + 0.08, 0.76);
  
  return {
    background: getGlassBackground(variant, colorMode, hoverOpacity),
    boxShadow: `
      0 24px 60px rgba(0, 0, 0, ${config.shadowOpacity + 0.08}),
      inset 0 1px 0 rgba(255, 255, 255, ${config.highlightOpacity + 0.12})
    `.trim(),
  };
}

// Tailwind class generator for glass effects
export function getGlassClasses(
  enabled: boolean,
  intensity: GlassIntensity = 'medium',
  isHover: boolean = false
): string {
  if (!enabled) {
    return 'bg-card border-border';
  }
  
  const blurClasses = {
    soft: 'backdrop-blur-md',
    medium: 'backdrop-blur-xl',
    deep: 'backdrop-blur-2xl',
  };
  
  const baseClasses = [
    'glass-panel',
    blurClasses[intensity],
    'border',
    'transition-all duration-300',
  ];
  
  if (isHover) {
    baseClasses.push('glass-panel-hover');
  }
  
  return baseClasses.join(' ');
}

// CSS custom properties for glass theme (to be injected into :root)
export const GLASS_CSS_VARS = {
  // Base glass properties
  '--glass-blur': '22px',
  '--glass-opacity': '0.58',
  '--glass-border-opacity': '0.48',
  '--glass-highlight-opacity': '0.3',
  '--glass-shadow-opacity': '0.2',
  '--glass-radius': '24px',
  '--glass-radius-sm': '14px',
  '--glass-radius-lg': '32px',
  
  // Light variant
  '--glass-bg-light': 'rgba(255, 255, 255, var(--glass-opacity))',
  '--glass-border-light': 'rgba(255, 255, 255, var(--glass-border-opacity))',
  '--glass-highlight-light': 'rgba(255, 255, 255, var(--glass-highlight-opacity))',
  
  // Dark variant
  '--glass-bg-dark': 'rgba(0, 0, 0, var(--glass-opacity))',
  '--glass-border-dark': 'rgba(0, 0, 0, var(--glass-border-opacity))',
  
  // Transition
  '--glass-transition': 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// Check if browser supports backdrop-filter
export function supportsBackdropFilter(): boolean {
  if (typeof window === 'undefined') return true;
  return CSS.supports('backdrop-filter', 'blur(1px)') || 
         CSS.supports('-webkit-backdrop-filter', 'blur(1px)');
}

// Check if user prefers reduced motion
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function prefersReducedTransparency(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-transparency: reduce)').matches ||
         window.matchMedia('(prefers-contrast: more)').matches;
}

export function isGlassSupported(): boolean {
  return supportsBackdropFilter() && !prefersReducedTransparency();
}

// Default glass configuration
export const DEFAULT_GLASS_CONFIG: GlassConfig = {
  enabled: true,
  intensity: 'medium',
  variant: 'regular',
};
